/**
 * The two ends of a single-choice field's value: what the `<select>` binds to,
 * and what gets stored back.
 *
 * A field nobody answered is `null` in the record, while the option that says
 * *nothing chosen* can only carry `''` — an `<option>` has no way to hold null.
 * The two have to be translated at the boundary, and both directions matter:
 *
 * - Bound as `null`, no option matches. The browser lands on `selectedIndex`
 *   -1 and draws an empty box, so the "—" meant to show that nothing is chosen
 *   is precisely what never appears.
 * - Stored as `''`, "never answered" and "answered with nothing" become two
 *   values for one state, which nothing downstream distinguishes — a required
 *   check, a filter and a `whereNull` each pick a different one.
 *
 * Real values keep their own type on the way in: coerced to a string, a numeric
 * option would stop matching the number the record holds.
 *
 * @see resources/js/components/base/ChoiceInput.vue
 */

/** What the empty option carries, since an `<option>` cannot hold null. */
export const EMPTY_CHOICE = '';

/** Record value → what the `<select>` binds to. */
export function boundChoiceValue(value) {
    return value ?? EMPTY_CHOICE;
}

/** What the `<select>` emitted → what the record stores. */
export function storedChoiceValue(value) {
    return value === EMPTY_CHOICE ? null : value;
}
